import { Checkbox } from '@design-system';
import { useTranslation } from 'next-i18next';
import { FC, FormEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { Frame, FrameOptionConsolidated } from '../../../../types/Products';
import { ShopifyProductToCheckout } from '../../../../types/Purchasing';
import { customPropertyByKey } from '../../../../utils/product';
import { FramePicker as FramePickerMain } from '../../../Containers/FramePicker/FramePicker';
import { ImageListItem } from '../ImageListItem/ImageListItem';

type FormValueOption = string | string[] | false;

export type FormValues = {
  option: FormValueOption;
};

export interface FramePickerProps {
  artistName: string;
  collectionTitle: string;
  defaultValues?: FormValues;
  frame: Frame | null;
  frameIncluded: boolean;
  frames: FrameOptionConsolidated[];
  onSelectFrame: (frame: Frame) => void;
  onSelectFrameVariant: (frame: Frame) => void;
  onSubmit: (data: FormValues) => void;
  orderItems: Record<string, ShopifyProductToCheckout>;
  storyblokProduct: ProductType;
}

const countFormValueOption = (formValueOption: FormValueOption): number => {
  if (!formValueOption) return 0;
  if (typeof formValueOption === 'string') return 1;
  return formValueOption.length;
};

export const FramePicker: FC<FramePickerProps> = ({
  artistName,
  collectionTitle,
  defaultValues = { option: false },
  frame,
  frames,
  onSelectFrame,
  onSelectFrameVariant,
  onSubmit,
  orderItems,
}) => {
  const { t } = useTranslation('nftPrintService');

  const form = useForm<FormValues>({ defaultValues });
  const options = form.watch('option');
  const selectedNft = typeof options === 'string' ? [options] : options;

  const handleSelectAll = useCallback(() => {
    form.setValue('option', Object.keys(orderItems));
  }, [form, orderItems]);

  const handleSubmit = useCallback(
    (ev: FormEvent<HTMLFormElement>) => {
      form.handleSubmit(onSubmit)(ev);
    },
    [form, onSubmit],
  );

  const productSubtotal = Object.values(orderItems).reduce(
    (acc, item) => acc + item.product.price * item.quantity,
    0,
  );

  return (
    <form
      className="mx-auto flex w-full max-w-md flex-col gap-4"
      onSubmit={handleSubmit}
    >
      <h6 className="text-md font-bold">{t('order.frameTitle')}</h6>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4 rounded-sm border border-divider/20 p-4">
          <div className="flex items-center justify-between">
            <div className="font-bold">{t('order.frameLabel')}</div>
            {Object.entries(orderItems).length <= 10 && (
              <button onClick={handleSelectAll} type="button">
                <span className="hyperlink">{t('selectAll')}</span>
              </button>
            )}
          </div>
          <div className="flex max-h-72 flex-col gap-3 overflow-y-auto overflow-x-hidden">
            {/* To-do: Hard to test/review this one – very likely a CheckboxCard would work */}
            {Object.entries(orderItems).map(([entryId, item]) => (
              <label
                key={`print-${entryId}`}
                className="group relative flex cursor-pointer items-center justify-between gap-3"
                htmlFor={`print-${entryId}`}
              >
                <ImageListItem
                  className="min-w-0 flex-shrink"
                  description={t('tokenId', {
                    id: customPropertyByKey(item.customProperties, 'token_id'),
                  })}
                  imageUris={customPropertyByKey(
                    item.customProperties,
                    '_token_img',
                  ).split(',')}
                  subtitle={collectionTitle}
                  title={artistName}
                  variant="product"
                />
                <div className="-mr-4 shrink-0">
                  <Checkbox
                    id={`print-option-${entryId}`}
                    type="checkbox"
                    {...form.register('option')}
                    checked={form.watch('option') === entryId}
                    label={`print-option-${entryId}`}
                    value={entryId}
                  />
                </div>
              </label>
            ))}
          </div>
        </div>
        {selectedNft && selectedNft.length > 0 && (
          <FramePickerMain
            description=""
            frameOptions={frames}
            frameSelected={frame}
            hideSubmitButton
            hideTitle
            includeUnframed={false}
            onFrameSelected={onSelectFrame}
            onVariantSelected={onSelectFrameVariant}
            productSubtotal={productSubtotal}
            submitButtonLabel={
              frame
                ? t('order.frameCta', {
                    count: countFormValueOption(selectedNft),
                  })
                : t('continue')
            }
          />
        )}
      </div>
    </form>
  );
};
